import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import i18n from '../../i18n';
import api from '../../services/api';

const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

/**
 * Catches a crashing page so the app never ends on a blank white screen: shows a way back (dashboard / reload), the
 * technical error, and reports it to the server log (POST /api/client-errors) for diagnosis.
 */
class Boundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        api.post('/client-errors', {
            message: String(error?.message || error).slice(0, 2000),
            stack: String(error?.stack || '').slice(0, 8000),
            componentStack: String(info?.componentStack || '').slice(0, 4000),
            url: window.location.href.slice(0, 500),
            userAgent: navigator.userAgent.slice(0, 500),
            standalone: standalone(),
        }).catch(() => {});
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;
        const t = i18n.t.bind(i18n);
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: '#F5F8FB' }}>
                <Box sx={{ maxWidth: 460, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1a2332', mb: 1 }}>{t('crash.title')}</Typography>
                    <Typography sx={{ fontSize: 14, color: '#64748B', mb: 3 }}>{t('crash.text')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                        <Button variant="contained" disableElevation onClick={() => { window.location.href = '/'; }}>{t('crash.home')}</Button>
                        <Button variant="outlined" onClick={() => window.location.reload()}>{t('crash.reload')}</Button>
                    </Box>
                    <Typography component="pre" sx={{
                        fontSize: 11, color: '#94A3B8', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left',
                        bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', p: 1.5, maxHeight: 160, overflow: 'auto',
                    }}>{String(error?.message || error)}</Typography>
                </Box>
            </Box>
        );
    }
}

/** Resets on navigation, so one crashed page does not block the others. */
export default function ErrorBoundary({ children }) {
    const location = useLocation();
    return <Boundary key={location.pathname}>{children}</Boundary>;
}
